/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { HrTime, Link, SpanKind, SpanStatus, SpanStatusCode } from "@opentelemetry/api";
import { InstrumentationScope } from "@opentelemetry/core";
import { Resource } from "@opentelemetry/resources";
import { ReadableSpan, TimedEvent } from "@opentelemetry/sdk-trace-base";

import Long = require("long");

import { dynatrace } from "../../gen/proto/odin-proto";
import * as SemConvTrace from "../../gen/dynatrace/odin/semconv/v1/SemConvTrace";

import { ComponentLogger } from "../Logger";
import { Attributes, AttributeValue } from "../utils/Compat";
import { verboseFormatException } from "../utils/LoggingUtils";
import { SendState } from "./SpanMetaData";
import { getMetaData, getFw4Tag, getPropagatedResourceAttributes } from "./SpanEmbedder";
import { Fw4Tag } from "../propagator/Fw4Tag";

// some shortcuts to inner namespaces in protocol
import pCollectorTrace = dynatrace.odin.proto.collector.trace.v1;
import pCollectorCommon = dynatrace.odin.proto.collector.common.v1;
import pCommon = dynatrace.odin.proto.common.v1;
import pTrace = dynatrace.odin.proto.trace.v1;
import pResource = dynatrace.odin.proto.resource.v1;

// ============================================================================

const logger = new ComponentLogger("Serialization");

// ============================================================================
/**
 * Serialize spans and resources into a protobuf message.
 * @param senderId The senderId
 * @param tenantUUID The Tenant UUID
 * @param spans The spans to serialize
 * @param resources The resources to include (serialized, @see serializeResources)
 * @returns serialized message
 */
export function serialize(senderId: Long, tenantUUID: string, spans: ReadableSpan[], resources: Buffer): Buffer {
	try {
		// create Span messages and map per traceId/serverId/...
		const spanMapper = new SpanMapper();
		for (const span of spans) {
			spanMapper.processSpan(span);
		}

		const spanExport = new pCollectorTrace.SpanExport();
		spanExport.tenantUUID = tenantUUID;
		spanExport.agentId = senderId;
		spanMapper.serializeSpans(spanExport);
		spanExport.exportMetaInfo = createExportMetaInfoMsg();
		spanExport.resource = resources;

		// TypeDefinition of protobufjs tell they return an UInt8Array but in case of Node it's a Buffer
		return pCollectorTrace.SpanExport.encode(spanExport).finish() as Buffer;
	} catch (e) {
		// ToDo: maybe implement a better error handling in exporter
		logger.error(`Failed ${verboseFormatException(e)}`);
	}
	return Buffer.alloc(0);
}


// ----------------------------------------------------------------------------
/**
 * Serialize Resource as Protobuf message.
 * @param resource The resources to serialize
 * @return The serialized Resource message
 */
export function serializeResource(resource: Resource): Buffer {
	try {
		const resourceMsg = new pResource.Resource();
		addAttributes(resourceMsg.attributes, resource.attributes);
		// TypeDefinition of protobufjs tell they return an UInt8Array but in case of Node it's a Buffer
		return pResource.Resource.encode(resourceMsg).finish() as Buffer;
	} catch (e) {
		logger.error(`Failed ${verboseFormatException(e)}`);
	}
	return Buffer.alloc(0);
}

// ----------------------------------------------------------------------------
function createExportMetaInfoMsg(): Uint8Array {
	const metaInfo = new pCollectorCommon.ExportMetaInfo();
	metaInfo.timeSyncMode = pCollectorCommon.ExportMetaInfo.TimeSyncMode.NTPSync;
	return pCollectorCommon.ExportMetaInfo.encode(metaInfo).finish();
}

// ----------------------------------------------------------------------------
function toSpanKindProto(kind: SpanKind): pTrace.Span.SpanKind {
	const SpanKindProto = pTrace.Span.SpanKind;
	switch (kind) {
		case SpanKind.INTERNAL: return SpanKindProto.INTERNAL;
		case SpanKind.SERVER: return SpanKindProto.SERVER;
		case SpanKind.CLIENT: return SpanKindProto.CLIENT;
		case SpanKind.PRODUCER: return SpanKindProto.PRODUCER;
		case SpanKind.CONSUMER: return SpanKindProto.CONSUMER;
		default:
			logger.debug(`invalid SpanKind: ${kind}`);
			return SpanKindProto.SPAN_KIND_UNSPECIFIED;
	}
}

// ----------------------------------------------------------------------------
function toStatusCodeProto(code: SpanStatusCode): pTrace.Status.StatusCode {
	const ProtoStatusCode = pTrace.Status.StatusCode;
	switch (code) {
		case SpanStatusCode.OK: return ProtoStatusCode.Ok;
		case SpanStatusCode.UNSET: return ProtoStatusCode.Ok;
		case SpanStatusCode.ERROR: return ProtoStatusCode.UnknownError;
		default:
			logger.debug(`invalid StatusCode: ${code}`);
			return ProtoStatusCode.Ok;
	}
}

// ----------------------------------------------------------------------------
function toSendReason(sendState: SendState): pTrace.Span.SendReason {
	const SendReason = pTrace.Span.SendReason;
	switch (sendState) {
		case SendState.New: return SendReason.NewOrChanged;
		case SendState.Drop: return SendReason.Dropped;
		case SendState.Update: return SendReason.NewOrChanged;
		case SendState.Alive: return SendReason.KeepAlive;
		case SendState.Finished: return SendReason.Ended;
		default:
			logger.debug(`invalid SendState: ${sendState}`);
			return SendReason.KeepAlive;
	}
}

// ----------------------------------------------------------------------------
function hrTimeToLong(hrTime: HrTime): Long {
	return Long.fromNumber(hrTime[0], true).mul(1e9).add(hrTime[1]);
}

// ----------------------------------------------------------------------------
function setAttrVal(msg: pCommon.AttributeKeyValue, val: unknown): boolean {
	const ValueType = pCommon.AttributeKeyValue.ValueType;

	if (Array.isArray(val)) {
		const firstNonNullVal = val.find(v => v != null);

		if (firstNonNullVal == null) {
			msg.type = ValueType.VALUELESS_ARRAY;
			return true;
		}

		switch (typeof(firstNonNullVal)) {
			case "number":
				msg.type = ValueType.DOUBLE_ARRAY;
				msg.double_values = val.map(v => typeof(v) === "number" ? v : 0);
				break;
			case "boolean":
				msg.type = ValueType.BOOL_ARRAY;
				msg.bool_values = val.map(v => typeof(v) === "boolean" ? v : false);
				break;
			case "string":
				msg.type = ValueType.STRING_ARRAY;
				msg.string_values = val.map(v => typeof(v) === "string" ? v : "");
				break;
			default:
				logger.debug(`invalid Array Attribute type: ${typeof(firstNonNullVal)}`);
				return false;
		}
		return true;
	}

	switch (typeof(val)) {
		case "number":
			msg.type = ValueType.DOUBLE;
			msg.double_value = val;
			break;
		case "boolean":
			msg.type = ValueType.BOOL;
			msg.bool_value = val;
			break;
		case "string":
			msg.type = ValueType.STRING;
			msg.string_value = val;
			break;
		default:
			logger.debug(`invalid Attribute type: ${typeof(val)}`);
			return false;
	}
	return true;
}

// ----------------------------------------------------------------------------
function addInstrumentationScope(attrPb: pCommon.IAttributeKeyValue[], lib: InstrumentationScope): void {
	const name = new pCommon.AttributeKeyValue();

	if (setAttrVal(name, lib.name)) {
		name.key = SemConvTrace.OTEL_LIBRARY_NAME;
		attrPb.push(name);
	}
	if (lib.version) {
		const version = new pCommon.AttributeKeyValue();
		version.key = SemConvTrace.OTEL_LIBRARY_VERSION;
		version.string_value = lib.version;
		attrPb.push(version);
	}
}

// ----------------------------------------------------------------------------
function addAttributes(attrPb: pCommon.IAttributeKeyValue[], attributes: Attributes): void {
	for (const key in attributes) {
		if (attributes.hasOwnProperty(key)) {
			addAttribute(attrPb, key, attributes[key]);
		}
	}
}

// ----------------------------------------------------------------------------
function addAttribute(attrPb: pCommon.IAttributeKeyValue[], key: string, value: AttributeValue | undefined): void {
	const attribMsg = new pCommon.AttributeKeyValue();
	if (setAttrVal(attribMsg, value)) {
		attribMsg.key = key;
		attrPb.push(attribMsg);
	}
}

// ----------------------------------------------------------------------------
function addSpanAttributes(attrPb: pCommon.IAttributeKeyValue[], span: ReadableSpan): void {
	const resourceAttrs = getPropagatedResourceAttributes(span);
	if (resourceAttrs != null) {
		for (const key in resourceAttrs) {
			if (resourceAttrs.hasOwnProperty(key)) {
				addAttribute(attrPb, key, resourceAttrs[key]);
			}
		}
	}

	let spanAttrs = span.attributes;
	const reasons = getFw4Tag(span.spanContext().traceState)?.extensions?.tcr?.reasons;
	if (reasons != null && reasons.length > 0) {
		spanAttrs = { ...spanAttrs, [SemConvTrace.TRACE_CAPTURE_REASONS]: reasons };
	}

	for (let key in spanAttrs) {
		if (!spanAttrs.hasOwnProperty(key)) {
			continue;
		}

		const value = spanAttrs[key];
		if (resourceAttrs?.hasOwnProperty(key)) {
			if (areAttributesEqual(resourceAttrs[key], value)) {
				continue;
			}

			// since we support at most 1 overwritten attribute per key here
			// we can hardcode the prefix to index 1
			key = `overwritten1.${key}`;
		}
		addAttribute(attrPb, key, value);
	}
}

// ------------------------------------------------------------------------
function areAttributesEqual(attr: AttributeValue | undefined, other: AttributeValue | undefined) : boolean {
	if (attr === other) {
		return true;
	}

	if (Array.isArray(attr) && Array.isArray(other)) {
		if (attr.length !== other.length) {
			return false;
		}
		for (let idx = 0; idx < attr.length; idx++) {
			if (attr[idx] !== other[idx]) {
				return false;
			}
		}
		return true;
	}

	return false;
}

// ----------------------------------------------------------------------------
function addEvents(spanMsg: pTrace.Span, events: TimedEvent[]): void {
	for (const event of events) {
		const eventMsg = new pTrace.Span.Event();
		eventMsg.name = event.name;
		eventMsg.time_unixnano = hrTimeToLong(event.time);
		if (event.attributes != null) {
			addAttributes(eventMsg.attributes, event.attributes);
		}
		spanMsg.events.push(eventMsg);
	}
}

// ----------------------------------------------------------------------------
function addLinks(spanMsg: pTrace.Span, links: Link[]): void {
	for (const link of links) {
		const linkMsg = new pTrace.Span.Link();
		linkMsg.trace_id = Buffer.from(link.context.traceId, "hex");
		linkMsg.span_id = Buffer.from(link.context.spanId, "hex");
		if (link.attributes != null) {
			addAttributes(linkMsg.attributes, link.attributes);
		}
		if (link.context.isRemote) {
			const fw4Tag = getFw4Tag(link.context.traceState);
			if (fw4Tag != null) {
				linkMsg.fwtag_encoded_link_id = fw4Tag.encodedLinkId;
			}
		}
		spanMsg.links.push(linkMsg);
	}
}

// ----------------------------------------------------------------------------
function addStatus(spanMsg: pTrace.Span, status: SpanStatus): void {
	const statusMsg = new pTrace.Status();
	statusMsg.code = toStatusCodeProto(status.code);
	if (status.message != null) {
		statusMsg.message = status.message;
	}
	spanMsg.status = statusMsg;
}

// ============================================================================

interface SpanEnvelopeInfo {
	pathInfo: number;
	customTags: pTrace.CustomTag[];
	spanContainer: pCollectorTrace.SpanContainer;
}

// There are three levels for sorting/grouping spans.
// 1. serverId => refers to a TraceId map
// 2. traceId => refers to a PathInfo map
// 3. PathInfo => holds the actual spans
// This nesting looks quite deep but usually there is just one PathInfo value per TraceId.
// Different PathIds per traceId happen only for the case that we have no Dynatrace tagging
// but we receive a traceId.
type PathInfoMap = Map<number, SpanEnvelopeInfo>;
type TraceIdMap = Map<string, PathInfoMap>;
type ServerIdMap = Map<number, TraceIdMap>;

// ----------------------------------------------------------------------------
/// Helper class to map spans to envelopes based on traceId/serverId
class SpanMapper {
	// ------------------------------------------------------------------------
	/**
	 * Serializes all spans into SpanExport message
	 * @param spanExport the target SpanExport message
	 */
	public serializeSpans(spanExport: pCollectorTrace.SpanExport): void {
		for (const [, pathInfoMap] of this._traceIdMap) {
			for (const [, spanInfo] of pathInfoMap) {
				this._addActivGateSpanEnvelope(spanExport, spanInfo);
			}
		}

		for (const [serverId, traceIdMap] of this._serverIdMap) {
			for (const [, pathInfoMap] of traceIdMap) {
				for (const [, spanInfo] of pathInfoMap) {
					this._addActivGateSpanEnvelope(spanExport, spanInfo, serverId);
				}
			}
		}
	}

	// ------------------------------------------------------------------------
	public processSpan(span: ReadableSpan): void {
		const spanMsg = new pTrace.Span();
		const metaData = getMetaData(span)!;

		// trace_id and span_id are always needed
		spanMsg.trace_id = Buffer.from(span.spanContext().traceId, "hex");
		spanMsg.span_id = Buffer.from(span.spanContext().spanId, "hex");

		// Remaining data is only needed for full updates and finished spans
		const sendState = metaData.sendState;
		if ((sendState === SendState.Finished) || (sendState === SendState.Update)) {
			if (metaData.tenantParentSpanId != null) {
				spanMsg.tenant_parent_span_id = Buffer.from(metaData.tenantParentSpanId, "hex");
			}
			if (metaData.encodedLinkId != null) {
				spanMsg.parent_fwtag_encoded_link_id = metaData.encodedLinkId;
			}
			if (span.parentSpanContext?.spanId != null) {
				spanMsg.parent_span_id = Buffer.from(span.parentSpanContext.spanId, "hex");
			}
			if (metaData.mobileTag != null) {
				spanMsg.mobile_tag = metaData.mobileTag;
			}
			spanMsg.name = span.name;
			spanMsg.kind = toSpanKindProto(span.kind);
			spanMsg.start_time_unixnano = hrTimeToLong(span.startTime);
			if (sendState === SendState.Finished) {
				spanMsg.end_time_unixnano = hrTimeToLong(span.endTime);
				if (metaData.lastPropagationTime != null) {
					spanMsg.last_propagate_time_unixnano = hrTimeToLong(metaData.lastPropagationTime);
				}
			}
			addInstrumentationScope(spanMsg.attributes, span.instrumentationScope);
			addSpanAttributes(spanMsg.attributes, span);
			addEvents(spanMsg, span.events);
			addLinks(spanMsg, span.links);
			addStatus(spanMsg, span.status);
		}
		spanMsg.send_reason = toSendReason(sendState);
		spanMsg.update_sequence_no = metaData.seqNr;

		this._addSpanToMap(span.spanContext().traceId, spanMsg, getFw4Tag(span.spanContext().traceState)!);
	}

	// ------------------------------------------------------------------------
	private _addSpanToMap(traceId: string, spanMsg: pTrace.Span, fw4Tag: Fw4Tag): void {
		const pathInfoMap = this._getPathInfoMap(fw4Tag.serverId, traceId);

		const pathInfo = fw4Tag.pathInfo;
		let info: SpanEnvelopeInfo | undefined = pathInfoMap.get(pathInfo);
		if (info == null) {
			info = {
				pathInfo: fw4Tag.pathInfo,
				customTags: this._createCustomTagsMsg(fw4Tag.extensions.customBlob),
				spanContainer: new pCollectorTrace.SpanContainer()
			};
			pathInfoMap.set(pathInfo, info);
		}
		info.spanContainer.spans.push(spanMsg);
	}

	// ------------------------------------------------------------------------
	private _getPathInfoMap(serverId: number, traceId: string): PathInfoMap {
		const traceIdMap = this._getTraceIdMap(serverId);

		let pathInfoMap = traceIdMap.get(traceId);
		if (pathInfoMap == null) {
			pathInfoMap = new Map();
			traceIdMap.set(traceId, pathInfoMap);
		}
		return pathInfoMap;
	}

	// ------------------------------------------------------------------------
	private _getTraceIdMap(serverId: number): TraceIdMap {
		if (serverId === 0) {
			return this._traceIdMap;
		}
		let traceIdMap = this._serverIdMap.get(serverId);
		if (traceIdMap == null) {
			traceIdMap = new Map();
			this._serverIdMap.set(serverId, traceIdMap);
		}
		return traceIdMap;
	}

	// ------------------------------------------------------------------------
	private _addActivGateSpanEnvelope(spanExport: pCollectorTrace.SpanExport, spans: SpanEnvelopeInfo, serverId?: number): void {
		const clusterSpanEnvelope = new pCollectorTrace.ClusterSpanEnvelope();
		clusterSpanEnvelope.traceId = spans.spanContainer.spans[0].trace_id!; // avoid reencoding
		clusterSpanEnvelope.pathInfo = spans.pathInfo;
		clusterSpanEnvelope.customTags = spans.customTags;
		clusterSpanEnvelope.spanContainer = pCollectorTrace.SpanContainer.encode(spans.spanContainer).finish();

		const agSpanEnvelope = new pCollectorTrace.ActiveGateSpanEnvelope();
		agSpanEnvelope.clusterSpanEnvelope = pCollectorTrace.ClusterSpanEnvelope.encode(clusterSpanEnvelope).finish();
		// routing by serverId is preferred
		if (serverId != null) {
			agSpanEnvelope.serverId = serverId;
		} else {
			agSpanEnvelope.traceId = clusterSpanEnvelope.traceId;
		}

		spanExport.spans.push(agSpanEnvelope);
	}

	// ------------------------------------------------------------------------
	private _createCustomTagsMsg(customBlob: string): pTrace.CustomTag[] {
		if (customBlob.length > 0) {
			const customTag = new pTrace.CustomTag();
			// NodeJs supports propagation of incoming custom tags only at this time
			customTag.direction = pTrace.CustomTag.Direction.Incoming;
			// first char in blob is the type (see OAAD tagging) and can be directly assigned
			// as enum values in ODIN match to OneAgent
			customTag.type = customBlob.charCodeAt(0);
			customTag.tagValue = Buffer.from(customBlob.substring(1), "binary");
			return [ customTag ];
		}
		return [];
	}

	// ------------------------------------------------------------------------
	private _traceIdMap: TraceIdMap = new Map<string, PathInfoMap>();
	private _serverIdMap: ServerIdMap = new Map<number, TraceIdMap>();
}
